import RouterStore from "stores/RouterStore";
import { getTokenByName } from "./../eth/contracts/contracts";
import UserStore from "stores/UserStore";
import { getContractByName } from "eth/contracts/contracts";
import { Contract, ethers } from "ethers";
import handleTransaction from "helpers/handleTransaction";
import { action, computed, observable, runInAction } from "mobx";
import EthStore from "./EthStore";
import { formatUnits } from "ethers/lib/utils";

type GovernanceUserData = {
  number: number;
  timelockedYGY: number;
};

class GovernanceStore {
  @observable weightedNumber: number = 0;
  @observable totalYGY: number = 0;
  @observable inStaging = false;
  @observable updateStagingReadyTime = 0;
  @observable lastRAMRouterUpdateTime = 0;
  @observable userData: Map<string, GovernanceUserData> = new Map();
  @observable timeLocks: Map<number, number> = new Map();
  @observable contract: Contract | any = null;

  @computed get stagingReadyDate() {
    return new Date(this.updateStagingReadyTime);
  }

  @computed get nextRamRouterUpdateTime() {
    return new Date(this.lastRAMRouterUpdateTime + 86400000);
  }

  @computed get regeneratorState() {
    const canInitiatateStaging = !this.inStaging && this.nextRamRouterUpdateTime <= new Date();
    const inStagingWaiting = this.inStaging && this.stagingReadyDate >= new Date();
    const canChangeFee = this.inStaging && this.stagingReadyDate <= new Date();
    if (canInitiatateStaging) return "default";
    else if (inStagingWaiting) return "staging-waiting";
    else if (canChangeFee) return "ready";
    else return "waiting-next";
  }

  @action initializeContract(network: string) {
    const governance = getContractByName("Governance", network);
    if (governance) {
      this.contract = new ethers.Contract(governance.address, governance.abi, EthStore.signer);
    } else {
      console.error("Could not find find contract, wrong network supplied?");
    }
  }

  @action getUpdateStagingReadyTime = async () => {
    if (this.contract) {
      const updateStagingReadyTime = Number(await this.contract.updateStagingReadyTime());
      runInAction(() => (this.updateStagingReadyTime = updateStagingReadyTime * 1000));
    }
  };

  @action getLastRAMRouterUpdateTime = async () => {
    if (this.contract) {
      const lastRAMRouterUpdateTime = Number(await this.contract.lastRAMRouterUpdateTime());
      runInAction(() => (this.lastRAMRouterUpdateTime = lastRAMRouterUpdateTime * 1000));
    }
  };

  @action getIsInStaging = async () => {
    if (this.contract) {
      const inStaging = await this.contract.updateStagingMode();
      runInAction(() => (this.inStaging = inStaging));
    }
  };

  initialize = async () => {
    await Promise.all([this.getWeightedNumber(), this.getTotalYGY(), this.getUserData()]);
    await this.getUpdateStagingReadyTime();
    await this.getLastRAMRouterUpdateTime();
    await this.getIsInStaging();
    await RouterStore.getRegeneratorTax();
  };

  @action async getUserData() {
    const userGovernanceData = await this.contract.users(EthStore.account);
    const mappedData = {
      number: Number(userGovernanceData.number),
      timelockedYGY: parseFloat(formatUnits(userGovernanceData.timelockedYGY, 6)),
    };
    runInAction(() => this.userData.set(EthStore.account, mappedData));

    const timelocks = await Promise.all([1, 2, 3, 4].map(async (level) => await this.contract.hasTimeLockAtLevel(EthStore.account, level)));
    timelocks.forEach((timelock) => {
      if (timelock._hasTimelock) {
        runInAction(() => this.timeLocks.set(Number(timelock._level), Number(timelock._unlockTime)));
      } else {
        runInAction(() => this.timeLocks.set(Number(timelock._level), 0));
      }
    });
    return;
  }

  @action async enterRegeneratorUpdateStagingMode() {
    await handleTransaction(async () => await this.contract.enterRegeneratorUpdateStagingMode(), `update regenererator to staging mode`);
    const inStaging = await this.contract.updateStagingMode();
    const updateStagingReadyTime = Number(await this.contract.updateStagingReadyTime());
    runInAction(() => {
      this.updateStagingReadyTime = updateStagingReadyTime * 1000;
      this.inStaging = inStaging;
    });
  }
  @action async updateRAMRouterRegeneratorTax() {
    await handleTransaction(async () => await this.contract.updateRAMRouterRegeneratorTax(), `update regenerator tax`);
    await this.initialize();
  }

  routeProposalSubmit = async (amount?: number, taxRateProposal?: number, withdraw?: boolean) => {
    if (amount && taxRateProposal) {
      this.depositLiquidYGYUpdateNumber(amount, taxRateProposal);
    } else if (taxRateProposal && !amount) {
      this.setUserNumber(taxRateProposal);
    } else if (amount) {
      this.depositLiquidYGY(amount);
    } else if (amount && withdraw) {
      this.withdrawLiquidYGY(amount);
    }
    await this.transactionCallback();
  };

  depositLiquidYGY = async (amount: number) => {
    const parsedAmount = ethers.utils.parseUnits(amount.toString(), 6);
    await handleTransaction(async () => await this.contract.depositLiquidYGY(parsedAmount), `governance deposit of ${amount} YGY`);
    await this.transactionCallback();
    return;
  };

  withdrawLiquidYGY = async (amount: number) => {
    const parsedAmount = ethers.utils.parseUnits(amount.toString(), 6);
    await handleTransaction(async () => await this.contract.withdrawLiquidYGY(parsedAmount), `governance withdraw of s${amount} YGY`);
    await this.transactionCallback();
    return;
  };

  timelockYGY = async (amount: number, level: number, taxValue: number) => {
    const parsedAmount = ethers.utils.parseUnits(amount.toString(), 6);
    await handleTransaction(
      async () => await this.contract.timelockYGY(parsedAmount, level, taxValue),
      `governance deposit timelock ${amount}YGY`
    );
    await this.transactionCallback();
    return;
  };

  depositLiquidYGYUpdateNumber = async (amount: number, number: number) => {
    const parsedAmount = ethers.utils.parseUnits(amount.toString(), 6);
    await handleTransaction(
      async () => await this.contract.depositLiquidYGYUpdateNumber(parsedAmount, number),
      `governance deposit of ${amount}YGY - tax rate proposal ${number}%`
    );
    await this.transactionCallback();
  };

  @action async getWeightedNumber() {
    const weightedNumber = await this.contract.weightedNumber();
    runInAction(() => (this.weightedNumber = Number(weightedNumber)));
    return;
  }

  @action async unlockOldestYGY(level: number) {
    try {
      await handleTransaction(async () => this.contract.unlockOldestTimelock(level), `unlocking YGY`);
      const YGYToken = getTokenByName("YGY", EthStore.network);
      if (YGYToken) {
        await UserStore.fetchBalance(EthStore.account, YGYToken, EthStore.provider);
      }
    } catch {
      console.log("not");
    }
  }

  @action getTotalYGY = async () => {
    const totalYGY = await this.contract.votingShares();
    runInAction(() => (this.totalYGY = parseFloat(formatUnits(totalYGY, 6))));
    return;
  };

  transactionCallback = async () => {
    await Promise.all([await this.getTotalYGY(), await this.getWeightedNumber(), await this.getUserData()]);
    return;
  };

  setUserNumber = async (number: number) => {
    await handleTransaction(async () => await this.contract.setUserNumber(number), `tax-rate proposal ${number}%`);
    await this.transactionCallback();
  };
}

export default new GovernanceStore();
